export interface RootState {
  version: string;
}

export interface RecipesState {
  detailInfo: RecipeDetailInfo | null;
  addRecipeResult: FavoriteOperationResult | null;
  deleteRecipeResult: FavoriteOperationResult | null;
  recentMyList: FavoriteRecipeClips | null;
  folderList: FavoriteFolders | null;
}

/**
 * レシピカテゴリー一覧
 */
export interface RecipeCategories {
  categories: Array<RecipeCategory>;
}

/**
 * レシピカテゴリーデータ
 */
export interface RecipeCategory {
  id: number;
  name: string;
  icon: string | null;
  note: string | null;
  startDate: string | null;
  endDate: string | null;
  children: Array<string> | null;
  isNew: boolean;
  image: string;
}

/**
 * レシピ一覧
 */
export interface Recipes {
  recipes: Array<Recipe>;
  totalCount: number;
  start: number;
}

/**
 * レシピデータ
 */
export interface Recipe {
  recipeId: number;
  groupId: number;
  title: string;
  type: string;
  applianceCategory: string;
  applianceCategoryId: number;
  images: Array<RecipeImage>;
  tags: Array<Tag>;
  productId: string;
  isNew: boolean;
  dishType: string;
  usedRequiredFoods: Array<number>;
}

/**
 * レシピ画像データ
 */
export interface RecipeImage {
  thumb: string;
  main: string;
  original: string;
}

/**
 * タグデータ
 */
export interface Tag {
  id: number;
  name: string;
}

/**
 * 栄養情報
 */
export class Nutrition {
  note: string | null;
  calories: number | null;
  salt: number | null;
  carbohydrate: number | null;
  lipid: number | null;
  protein: number | null;
  constructor() {
    this.note = null;
    this.calories = null;
    this.salt = null;
    this.carbohydrate = null;
    this.lipid = null;
    this.protein = null;
  }
}

/**
 * 付属品情報
 */
export class Attachment {
  id: string | null;
  description: string | null;
  constructor() {
    this.id = null;
    this.description = null;
  }
}

/**
 * 献立情報
 */
export interface Variation {
  num: number;
  unit: string;
}

export interface Theme {
  themeId: number;
  themeName: string;
}

export interface Instruction {
  step: number;
  description: string;
  note: string;
  images: Array<RecipeImage>;
  video: string;
  dlMenuId: number;
  touch: boolean;
}

export interface RefIndex {
  refMenuIndex: number;
  refRecipeIndexes: Array<RefRecipeIndex>;
}

export interface RefRecipeIndex {
  refRecipeIndex: number;
  refIngredientIndexes: string;
}

export interface Item {
  description: string;
  quantity: string;
  unit: string;
  quantityText: string;
  refIndexes: Array<RefIndex>;
  item: Array<Item>;
}

export interface Ingredient {
  description: string;
  quantity: string;
  unit: string;
  quantityText: string;
  refIndexes: Array<RefIndex>;
  item: Array<Item>;
}

export interface Dish {
  title: string;
  cookingTool: string;
  nutrition: Nutrition;
  ingredients: Array<Ingredient>;
}

export class Content {
  servings: Variation | null;
  theme: Theme | null;
  dishes: Array<Dish> | null;
  instructions: Array<Instruction> | null;
  constructor() {
    this.servings = null;
    this.theme = null;
    this.dishes = null;
    this.instructions = null;
  }
}

export interface Author {
  name: string;
  forDisplay: string;
}

export interface Like {
  done: boolean;
  count: number;
}

export interface Favorite {
  done: boolean;
  count: number;
}

export interface MyComment {
  done: boolean;
  count: number;
}

export class Reaction {
  like: Like | null;
  favorite: Favorite | null;
  myComment: MyComment | null;
  constructor() {
    this.like = null;
    this.favorite = null;
    this.myComment = null;
  }
}

export interface TitleLabel {
  description: string;
  fgColor: string;
  bgColor: string;
}

/**
 * レシピ詳細情報
 */
export interface DetailInfo {
  recipeId: number;
  groupId: number;
  title: string;
  subTitle: string;
  keyword: string;
  description: string;
  tags: Array<Tag>;
  applianceCategoryId: number;
  applianceCategory: string;
  productId: string;
  attachments: Array<Attachment>;
  dishType: string;
  nutrition: Nutrition;
  notes: Array<string>;
  video: string;
  variation: Array<Variation>;
  themes: Array<Theme>;
  content: Content;
  myAppliance: string;
  myApplianceRecipe: boolean;
  forMenu: boolean;
  type: string;
  images: Array<RecipeImage>;
  author: Author;
  reactions: Reaction;
  titleLabel: TitleLabel;
  status: string;
}

export class RequestRecipeDetail {
  id: number;
  type: string;
  servings: number | null;
  theme: number | null;
  applianceList: Array<string> | null;
  constructor(id: number,
    type: string,
    servings: number | null,
    theme: number | null,
    applianceList: Array<string> | null) {
    this.id = id;
    this.type = type;
    this.servings = servings;
    this.theme = theme;
    this.applianceList = applianceList;
  }
}

export class RecipeDetailInfo {
  info: DetailInfo | null;
  constructor(info: DetailInfo | null) {
    this.info = info;
  }
}

export class DishItem {
  id: number;
  title: string;
  cookingTool: string;
  dish: Dish;
  ingredients: Array<Ingredient>;
  constructor(id: number, dish: Dish, ingredients: Array<Ingredient>) {
    this.id = id;
    this.title = "";
    if (dish && dish.title) {
      this.title += dish.title;
    }
    this.cookingTool = "";
    if (dish && dish.cookingTool) {
      this.cookingTool = dish.cookingTool;
    }
    this.dish = dish;
    this.ingredients = ingredients;
  }
}

export class IngredientItem {
  id: number;
  ingredient: Ingredient;
  items: Array<Item>;
  top: boolean;
  bottom: boolean;
  constructor(id: number, ingredient: Ingredient, items: Array<Item>, top: boolean, bottom: boolean) {
    this.id = id;
    this.ingredient = ingredient;
    this.items = items;
    this.top = top;
    this.bottom = bottom;
  }
}

export class IngredientSubItem {
  id: number;
  item: Item;
  constructor(id: number, item: Item) {
    this.id = id;
    this.item = item;
  }
}

export class InstructionItem {
  id: number;
  item: Instruction;
  lastItem: boolean;
  constructor(id: number, item: Instruction, lastItem: boolean) {
    this.id = id;
    this.item = item;
    this.lastItem = lastItem;
  }
}

/** ドロップダウン種別 */
export const RECIPE_DROPDOWN_TYPE = {
  /** variation */
  VARIATION: "variation",
  /** theme */
  THEME: "theme"
};

export type RECIPE_DROPDOWN_TYPE = typeof RECIPE_DROPDOWN_TYPE[keyof typeof RECIPE_DROPDOWN_TYPE];

/** ボトムアクションバー種別 */
export const BOTTOM_ACTION_TYPE = {
  /** 材料 */
  INGREDIENTS: "ingredients",
  /** 前の工程 */
  PREVIOUS_STEP: "previous_step",
  /** 次の工程 */
  NEXT_STEP: "next_step",
  /** コメント */
  COMMENT: "comment"
} as const;

export const FOLDER_ID_ALL = -1;

/**
 * API お気に入りフォルダ・レシピ追加/削除のレスポンス
 */
 export class FavoriteOperationResult {
  status: boolean;

  constructor() {
    this.status = true;
  }
}

/**
 * フォルダ表示定義
 */
export class FolderInfo {
  id: number;
  name: string;
  image: string;
  numberOfItems: number;
  constructor() {
    this.id = 0;
    this.name = "";
    this.image = "";
    this.numberOfItems = 0;
  }
}

/**
 * API お気に入りフォルダ内のレシピ情報のタグ情報
 */
 export interface FavoriteRecipeTag {
  id: number;
  name: string;
}
/**
 * API お気に入りフォルダ内のレシピ情報 レシピ定義
 */
 export class FavoriteRecipeClip {
  recipeId: number;
  caption: string;
  imageUrl: string;
  moduleType: string;
  device: string;
  applianceCategoryId: number;
  productId: string;
  groupId: number;
  type: string;
  tags: Array<FavoriteRecipeTag>;

  constructor() {
    this.recipeId = 0;
    this.caption = "";
    this.imageUrl = "";
    this.moduleType = "";
    this.device = "";
    this.applianceCategoryId = 0;
    this.productId = "";
    this.groupId = 0;
    this.type = "";
    this.tags = [];
  }
}

/**
 * お気に入りのレシピ取得結果
 */
export class FavoriteRecipeClips {
  clips: Array<FavoriteRecipeClip>;
  totalCount: number;
  start: number;
  lastUpdate: string;

  constructor() {
    this.clips = [];
    this.totalCount = 0;
    this.start = 0;
    this.lastUpdate = "";
  }
}

/**
 * API お気に入りフォルダ一覧の要素定義
 */
interface FavoriteFolder {
  favoriteFolderId: number;
  name: string;
  imageUrl: string;
  clipCount: number;
}

/**
 * API お気に入りフォルダ一覧取得のレスポンス定義
 */
export class FavoriteFolders {
  folders: Array<FavoriteFolder>;
  folderCount: number;

  constructor() {
    this.folders = [];
    this.folderCount = 0;
  }
}

/** HOMEレシピ種別 */
export const LINK_TYPE = {
  SHOW_APPLIANCE_LIST: "show_appliance_list",
  SHOW_ATTACHMENT_LIST: "show_attachment_list"
} as const;